// src/components/Exercise/StepByStep/StepByStepMath.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Card,
  CardContent,
  Chip,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Lightbulb,
  Calculate,
  PlayArrow,
  Refresh
} from '@mui/icons-material';

interface MathStep {
  id: string;
  description: string;
  expression: string;
  latex?: string;
  explanation: string;
  userInput: string;
  isCorrect: boolean | null;
  hint?: string;
  allowedAnswers?: string[];
}

interface StepByStepConfig {
  exerciseType: 'step_by_step_math';
  problemStatement: string;
  problemLatex?: string;
  steps: MathStep[];
  finalAnswer: string | number;
  difficulty: number;
  timeLimit?: number;
  allowCalculator: boolean;
  hints: string[];
}

interface Props {
  config: StepByStepConfig;
  onSubmit: (answers: string[], steps: MathStep[]) => void;
  readonly?: boolean;
  initialAnswers?: string[];
}

const StepByStepMath: React.FC<Props> = ({ 
  config, 
  onSubmit, 
  readonly = false,
  initialAnswers = []
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [steps, setSteps] = useState<MathStep[]>(
    config.steps.map((step, index) => ({
      ...step,
      userInput: initialAnswers[index] || '',
      isCorrect: null
    }))
  );
  const [showHints, setShowHints] = useState<boolean[]>(
    new Array(config.steps.length).fill(false)
  );
  const [isCompleted, setIsCompleted] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [startTime] = useState(Date.now());

  useEffect(() => {
    // Check if all steps are completed
    const allCompleted = steps.every(step => step.isCorrect === true);
    setIsCompleted(allCompleted);
  }, [steps]);

  const validateStep = (stepIndex: number, userAnswer: string): boolean => {
    const step = steps[stepIndex];
    
    // Simple validation - can be enhanced with math-expression-evaluator
    const normalizedAnswer = userAnswer.trim().toLowerCase().replace(/\s/g, '');
    const correctAnswers = step.allowedAnswers || [step.expression.replace(/[^0-9.-]/g, '')];
    
    return correctAnswers.some(answer => 
      normalizedAnswer === answer.toLowerCase().replace(/\s/g, '')
    );
  };

  const handleStepSubmit = (stepIndex: number) => {
    const step = steps[stepIndex];
    const isCorrect = validateStep(stepIndex, step.userInput);
    
    const updatedSteps = [...steps];
    updatedSteps[stepIndex] = { ...step, isCorrect };
    setSteps(updatedSteps);

    if (isCorrect && stepIndex < steps.length - 1) {
      setCurrentStep(stepIndex + 1);
    }
  };

  const handleInputChange = (stepIndex: number, value: string) => {
    const updatedSteps = [...steps];
    updatedSteps[stepIndex] = { 
      ...updatedSteps[stepIndex], 
      userInput: value,
      isCorrect: null // Reset validation when input changes
    };
    setSteps(updatedSteps);
  };

  const toggleHint = (stepIndex: number) => {
    const newShowHints = [...showHints];
    newShowHints[stepIndex] = !newShowHints[stepIndex];
    setShowHints(newShowHints);
  };

  const resetExercise = () => {
    const resetSteps = config.steps.map(step => ({
      ...step,
      userInput: '',
      isCorrect: null
    }));
    setSteps(resetSteps);
    setCurrentStep(0);
    setIsCompleted(false);
    setShowHints(new Array(config.steps.length).fill(false));
  };

  const getStepIcon = (stepIndex: number) => {
    const step = steps[stepIndex];
    if (step.isCorrect === true) return <CheckCircle color="success" />;
    if (step.isCorrect === false) return <Cancel color="error" />;
    if (stepIndex === currentStep) return <PlayArrow color="primary" />;
    return <Calculate />;
  };

  const handleFinalSubmit = () => {
    const answers = steps.map(step => step.userInput);
    onSubmit(answers, steps);
  };

  return (
    <Box p={3}>
      {/* Problem Statement */}
      <Card elevation={3} sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2}>
            <Calculate sx={{ fontSize: '2rem' }} />
            <Box>
              <Typography variant="h5" gutterBottom>
                📐 Ratkaise vaiheittain
              </Typography>
              <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>
                {config.problemStatement}
              </Typography>
              <Chip 
                label={`Vaikeustaso: ${config.difficulty}/5`}
                sx={{ mt: 1, backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Progress Overview */}
      <Box mb={3}>
        <Typography variant="h6" gutterBottom>
          📊 Edistyminen: {steps.filter(s => s.isCorrect === true).length}/{steps.length} vaihetta
        </Typography>
        <Box display="flex" gap={1} mb={2}>
          {steps.map((step, index) => (
            <Chip
              key={step.id}
              label={`${index + 1}`}
              color={
                step.isCorrect === true ? 'success' :
                step.isCorrect === false ? 'error' :
                index === currentStep ? 'primary' : 'default'
              }
              variant={index === currentStep ? 'filled' : 'outlined'}
            />
          ))}
        </Box>
      </Box>

      {/* Step-by-Step Solver */}
      <Stepper activeStep={currentStep} orientation="vertical">
        {steps.map((step, index) => (
          <Step key={step.id}>
            <StepLabel icon={getStepIcon(index)}>
              <Typography variant="h6">
                {step.description}
              </Typography>
            </StepLabel>
            <StepContent>
              <Paper elevation={1} sx={{ p: 3, mb: 2 }}>
                <Typography variant="body1" gutterBottom>
                  <strong>📝 Tehtävä:</strong> {step.explanation}
                </Typography>
                
                {/* Mathematical Expression */}
                <Box 
                  sx={{ 
                    fontFamily: 'monospace', 
                    fontSize: '1.2rem', 
                    backgroundColor: '#f5f5f5',
                    padding: 2,
                    borderRadius: 1,
                    mb: 2,
                    border: '2px dashed #ddd'
                  }}
                >
                  {step.expression}
                </Box>

                {/* User Input */}
                <Box display="flex" gap={2} alignItems="center" mb={2}>
                  <TextField
                    label="Vastauksesi"
                    variant="outlined"
                    value={step.userInput}
                    onChange={(e) => handleInputChange(index, e.target.value)}
                    disabled={readonly || step.isCorrect === true}
                    sx={{ flexGrow: 1 }}
                    helperText={
                      step.isCorrect === true ? "✅ Oikein!" :
                      step.isCorrect === false ? "❌ Väärin, yritä uudelleen" :
                      "Syötä vastauksesi tähän"
                    }
                    error={step.isCorrect === false}
                  />
                  
                  {!readonly && step.isCorrect !== true && (
                    <Button
                      variant="contained"
                      onClick={() => handleStepSubmit(index)}
                      disabled={!step.userInput.trim()}
                    >
                      Tarkista
                    </Button>
                  )}
                </Box>

                {/* Hint System */}
                {step.hint && (
                  <Box mb={2}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Tooltip title="Näytä vihje">
                        <IconButton 
                          onClick={() => toggleHint(index)}
                          color="info"
                        >
                          <Lightbulb />
                        </IconButton>
                      </Tooltip>
                      <Typography variant="body2" color="text.secondary">
                        Tarvitsetko vihjeen?
                      </Typography>
                    </Box>
                    
                    {showHints[index] && (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        <strong>💡 Vihje:</strong> {step.hint}
                      </Alert>
                    )}
                  </Box>
                )}

                {/* Step Status */}
                {step.isCorrect === true && (
                  <Alert severity="success">
                    🎉 Hienoa! Tämä vaihe on oikein. Siirry seuraavaan vaiheeseen.
                  </Alert>
                )}
                
                {step.isCorrect === false && (
                  <Alert severity="error">
                    🤔 Ei aivan oikein. Tarkista laskutoimituksesi ja yritä uudelleen.
                  </Alert>
                )}
              </Paper>
            </StepContent>
          </Step>
        ))}
      </Stepper>

      {/* Final Submission */}
      {isCompleted && !readonly && (
        <Card elevation={3} sx={{ mt: 3, background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
          <CardContent>
            <Typography variant="h5" gutterBottom sx={{ color: 'white' }}>
              🎊 Onnittelut! Olet ratkaissut tehtävän!
            </Typography>
            <Typography variant="body1" sx={{ color: 'white', mb: 2 }}>
              Kaikki vaiheet suoritettu oikein. Hienoa työtä!
            </Typography>
            <Box display="flex" gap={2}>
              <Button 
                variant="contained" 
                color="success"
                onClick={handleFinalSubmit}
                sx={{ backgroundColor: 'white', color: 'primary.main', '&:hover': { backgroundColor: '#f0f0f0' } }}
              >
                📤 Lähetä vastaus
              </Button>
              <Button 
                variant="outlined" 
                onClick={resetExercise}
                sx={{ borderColor: 'white', color: 'white', '&:hover': { borderColor: '#f0f0f0' } }}
                startIcon={<Refresh />}
              >
                🔄 Aloita alusta
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Exercise Info */}
      <Box mt={3} textAlign="center">
        <Typography variant="body2" color="text.secondary">
          {config.allowCalculator ? "🧮 Laskin sallittu" : "🚫 Ei laskinta"} • 
          {config.timeLimit ? ` ⏱️ Aikaraja: ${config.timeLimit} min` : " ⏰ Ei aikarajaa"} • 
          💡 {config.hints.length} vihjettä saatavilla
        </Typography>
      </Box>
    </Box>
  );
};

export default StepByStepMath;